import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { coinsApi, clubsApi, paypalApi, adsApi } from '../lib/api';
import { PremiumClubCard } from '../components/PremiumClubCard';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Coins,
  CreditCard,
  Shield,
  CheckCircle,
  XCircle,
  Loader2,
  Sparkles,
  Crown,
  Zap,
  User,
  Ban,
  Eye,
  EyeOff,
} from 'lucide-react';

const Shop = () => {
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [packages, setPackages] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [purchaseType, setPurchaseType] = useState('personal'); // 'personal' or 'club'
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  
  // PayPal state
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Ads removal state
  const [adsStatus, setAdsStatus] = useState({ ads_removed: false, removal_cost: 50000 });
  const [removingAds, setRemovingAds] = useState(false);

  // Check if user is captain
  const isCaptain = clubs.length > 0;

  // Load packages and clubs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [packagesRes, clubsRes] = await Promise.all([
          coinsApi.getPackages(),
          clubsApi.list(),
        ]);
        setPackages(packagesRes.data);
        
        // Filter clubs - show only clubs where user is captain
        const userClubs = clubsRes.data.filter(c => c.captain_id === user?.user_id);
        setClubs(userClubs);
        
        // Pre-select purchase type and club
        if (userClubs.length > 0) {
          setSelectedClub(userClubs[0].club_id);
          setPurchaseType('club'); // Default to club if captain
        } else {
          setPurchaseType('personal'); // Players buy for themselves
        }
        
        // Try to get PayPal client ID
        try {
          const paypalRes = await paypalApi.getClientId();
          setPaypalClientId(paypalRes.data.client_id);
        } catch (err) {
          console.log('PayPal not configured');
        }
        
        // Fetch ads removal status
        try {
          const adsRes = await adsApi.getStatus();
          setAdsStatus(adsRes.data);
        } catch (err) {
          console.log('Error fetching ads status');
        }
      } catch (error) {
        console.error('Error loading shop data:', error);
        toast.error(isItalian ? 'Errore nel caricamento' : 'Error loading data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isItalian]);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setPaymentResult({
        status: 'timeout',
        message: isItalian 
          ? 'Timeout nel controllo del pagamento. Controlla la tua email per conferma.'
          : 'Payment check timed out. Please check your email for confirmation.',
      });
      return;
    }

    try {
      const response = await coinsApi.getCheckoutStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        const isPremium = data.purchase_type === 'club_premium';
        setPaymentResult({
          status: 'success',
          message: isPremium
            ? (isItalian ? 'Pagamento completato! Il tuo club è ora Premium! 👑' : 'Payment successful! Your club is now Premium! 👑')
            : (isItalian
              ? `Pagamento completato! ${data.coins_added?.toLocaleString()} coins aggiunti!`
              : `Payment successful! ${data.coins_added?.toLocaleString()} coins added!`),
          coins: data.coins_added,
        });
        // Refresh user data
        if (refreshUser) refreshUser();
        // Clear URL params
        setSearchParams({});
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        setPaymentResult({
          status: 'expired',
          message: isItalian 
            ? 'La sessione di pagamento è scaduta. Riprova.'
            : 'Payment session expired. Please try again.',
        });
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
      setPaymentResult({
        status: 'error',
        message: isItalian 
          ? 'Errore nel controllo del pagamento.'
          : 'Error checking payment status.',
      });
    }
  }, [isItalian, refreshUser, setSearchParams]);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info(isItalian ? 'Pagamento annullato' : 'Payment cancelled');
      setSearchParams({});
    }
  }, [searchParams, pollPaymentStatus, isItalian, setSearchParams]);

  const handlePurchase = async (packageId) => {
    // For club purchase, club must be selected
    if (purchaseType === 'club' && !selectedClub) {
      toast.error(isItalian ? 'Seleziona un club' : 'Please select a club');
      return;
    }
    
    // Open payment method selection modal
    setSelectedPackage(packages.find(p => p.package_id === packageId));
    setShowPaymentModal(true);
  };

  const handleStripePayment = async () => {
    if (!selectedPackage) return;
    
    setPurchasing(true);
    setShowPaymentModal(false);
    try {
      const response = await coinsApi.createCheckout({
        package_id: selectedPackage.package_id,
        club_id: purchaseType === 'club' ? selectedClub : null,
        purchase_type: purchaseType,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nel pagamento' : 'Payment error'));
      setPurchasing(false);
    }
  };

  // PayPal redirect flow
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [retryingCapture, setRetryingCapture] = useState(false);

  const handlePayPalPayment = async () => {
    if (!selectedPackage) return;
    setPaypalLoading(true);
    try {
      const response = await paypalApi.createOrder({
        package_id: selectedPackage.package_id,
        club_id: purchaseType === 'club' ? selectedClub : null,
        purchase_type: purchaseType,
        origin_url: window.location.origin,
      });
      if (response.data.approve_url) {
        window.location.href = response.data.approve_url;
      } else {
        toast.error(isItalian ? 'Errore: URL PayPal non disponibile' : 'Error: PayPal URL not available');
      }
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore PayPal' : 'PayPal error'));
    }
    setPaypalLoading(false);
  };

  // Handle PayPal return
  useEffect(() => {
    const paypalSuccess = searchParams.get('paypal_success');
    const paypalCancel = searchParams.get('paypal_cancel');
    const paypalToken = searchParams.get('token');

    if (paypalSuccess && paypalToken) {
      setCheckingPayment(true);
      const capturePayPal = async () => {
        // Wait for auth context and PayPal to fully process approval
        await new Promise(r => setTimeout(r, 2000));
        
        let retries = 4;
        let lastError = null;
        
        while (retries >= 0) {
          try {
            const response = await paypalApi.captureOrder({ order_id: paypalToken });
            setPaymentResult({
              status: 'success',
              message: isItalian 
                ? `Pagamento PayPal completato! ${response.data.coins_added?.toLocaleString()} coins aggiunti!`
                : `PayPal payment successful! ${response.data.coins_added?.toLocaleString()} coins added!`,
              coins: response.data.coins_added,
            });
            if (refreshUser) refreshUser();
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            console.error(`PayPal capture attempt ${4 - retries + 1}:`, error?.response?.data);
            retries--;
            if (retries >= 0) {
              await new Promise(r => setTimeout(r, 3000));
            }
          }
        }
        
        if (lastError) {
          setPaymentResult({
            status: 'error',
            message: lastError.response?.data?.detail || (isItalian ? 'Errore nel pagamento PayPal. Prova il pulsante "Riprova".' : 'PayPal payment error. Try the "Retry" button.'),
            orderId: paypalToken,
          });
        }
        
        setCheckingPayment(false);
        setSearchParams({});
      };
      capturePayPal();
    } else if (paypalCancel) {
      toast.info(isItalian ? 'Pagamento PayPal annullato' : 'PayPal payment cancelled');
      setSearchParams({});
    }
  }, [searchParams, isItalian, refreshUser, setSearchParams]);

  const getPackageIcon = (packageId) => {
    switch (packageId) {
      case 'small': return <Coins className="w-8 h-8" />;
      case 'medium': return <Sparkles className="w-8 h-8" />;
      case 'large': return <Crown className="w-8 h-8" />;
      default: return <Coins className="w-8 h-8" />;
    }
  };

  const getPackageColor = (packageId) => {
    switch (packageId) {
      case 'small': return 'from-[#2979FF] to-[#00B0FF]';
      case 'medium': return 'from-[#00E676] to-[#00C853]';
      case 'large': return 'from-[#FFEA00] to-[#FF9100]';
      default: return 'from-[#2979FF] to-[#00B0FF]';
    }
  };

  const selectedClubData = clubs.find(c => c.club_id === selectedClub);

  // Can purchase if personal mode OR (club mode AND club selected)
  const canPurchase = purchaseType === 'personal' || (purchaseType === 'club' && selectedClub);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <Coins size={32} className="text-[#FFEA00]" />
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">
            {isItalian ? 'Acquista Coins' : 'Buy Coins'}
          </h1>
        </div>
        <p className="text-slate-400 max-w-xl mx-auto">
          {isItalian 
            ? 'Acquista coins per te o per il tuo club!'
            : 'Buy coins for yourself or your club!'}
        </p>
      </motion.div>

      {/* Premium Club */}
      <PremiumClubCard
        user={user}
        isItalian={isItalian}
        isCaptain={isCaptain}
        onActivated={(spentCoins) => {
          setClubs(prev => prev.map(c => c.club_id === user?.club_id ? { ...c, coins: (c.coins || 0) - spentCoins } : c));
        }}
      />

      {/* Purchase Type Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="max-w-md mx-auto"
      >
        <label className="block text-sm font-medium text-slate-300 uppercase tracking-wider mb-2">
          {isItalian ? 'Acquista Per' : 'Purchase For'}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setPurchaseType('personal')}
            className={`p-4  border transition-all ${
              purchaseType === 'personal'
                ? 'bg-[#00F0FF]/20 border-[#00F0FF] text-white'
                : 'bg-[#0a0d15] border-white/10 text-slate-400 hover:border-white/30'
            }`}
            data-testid="purchase-type-personal"
          >
            <User size={24} className="mx-auto mb-2" />
            <p className="font-medium">{isItalian ? 'Personale' : 'Personal'}</p>
            <p className="text-xs mt-1 opacity-70">{(user?.personal_coins || 0).toLocaleString()} coins</p>
          </button>
          
          {isCaptain && (
            <button
              onClick={() => setPurchaseType('club')}
              className={`p-4  border transition-all ${
                purchaseType === 'club'
                  ? 'bg-[#00E676]/20 border-[#00E676] text-white'
                  : 'bg-[#0a0d15] border-white/10 text-slate-400 hover:border-white/30'
              }`}
              data-testid="purchase-type-club"
            >
              <Shield size={24} className="mx-auto mb-2" />
              <p className="font-medium">Club</p>
              <p className="text-xs mt-1 opacity-70">{selectedClubData?.coins?.toLocaleString() || 0} coins</p>
            </button>
          )}
        </div>

        {/* Club Selection - only for captains buying for club */}
        {purchaseType === 'club' && isCaptain && (
          <div className="mt-4">
            <Select value={selectedClub} onValueChange={setSelectedClub} modal={false}>
              <SelectTrigger 
                className="bg-[#0a0d15] border-white/10 text-white"
                data-testid="club-select"
              >
                <SelectValue placeholder={isItalian ? 'Scegli un club...' : 'Choose a club...'} />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0d15] border-white/10 z-[100]">
                {clubs.map((club) => (
                  <SelectItem key={club.club_id} value={club.club_id} className="text-white hover:bg-white/10 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: club.colors?.primary || '#2979FF' }}
                      />
                      {club.name}
                      <span className="text-slate-500 text-xs">
                        ({club.coins?.toLocaleString()} coins)
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-4 p-4 bg-[#0a0d15] border border-white/10 ">
          <div className="flex items-center gap-3">
            {purchaseType === 'personal' ? (
              <>
                <div className="w-10 h-10 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                  <User size={20} className="text-[#00F0FF]" />
                </div>
                <div>
                  <p className="font-medium text-white">{isItalian ? 'Coins Personali' : 'Personal Coins'}</p>
                  <p className="text-xs text-slate-500">
                    {isItalian ? 'Per svincolarti, rimuovere pubblicità, ecc.' : 'For releasing, removing ads, etc.'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div 
                  className="w-10 h-10 rounded flex items-center justify-center"
                  style={{ backgroundColor: selectedClubData?.colors?.primary || '#00E676' }}
                >
                  <Shield size={20} className="text-white" />
                </div>
                <div>
                  <p className="font-medium text-white">{selectedClubData?.name || 'Club'}</p>
                  <p className="text-xs text-slate-500">
                    {isItalian ? 'Per iscriversi ai campionati, acquisti, ecc.' : 'For championships, purchases, etc.'}
                  </p>
                </div>
              </>
            )}
            <div className="ml-auto text-right">
              <p className="text-xs text-slate-500">{isItalian ? 'Saldo' : 'Balance'}</p>
              <p className="font-mono text-lg text-[#FFEA00]">
                {purchaseType === 'personal' 
                  ? ((user?.personal_coins || 0).toLocaleString())
                  : (selectedClubData?.coins?.toLocaleString() || 0)
                }
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Packages */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {packages.map((pkg, index) => (
          <motion.div
            key={pkg.package_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
            className={`relative bg-[#0a0d15] border border-white/10  overflow-hidden hover:border-white/20 transition-all ${
              pkg.package_id === 'large' ? 'ring-2 ring-[#FFEA00]/50' : ''
            }`}
            data-testid={`package-${pkg.package_id}`}
          >
            {/* Popular badge for large package */}
            {pkg.package_id === 'large' && (
              <div className="absolute top-0 right-0 bg-[#FFEA00] text-black px-3 py-1 text-xs font-bold uppercase">
                {isItalian ? 'Popolare' : 'Popular'}
              </div>
            )}

            {/* Gradient header */}
            <div className={`h-2 bg-gradient-to-r ${getPackageColor(pkg.package_id)}`} />

            <div className="p-6 text-center">
              {/* Icon */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${getPackageColor(pkg.package_id)} flex items-center justify-center text-white`}>
                {getPackageIcon(pkg.package_id)}
              </div>

              {/* Package name */}
              <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wider mb-2">
                {pkg.name}
              </h3>

              {/* Coins amount */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <Coins size={20} className="text-[#FFEA00]" />
                <span className="font-mono text-2xl font-bold text-white">
                  {pkg.coins.toLocaleString()}
                </span>
              </div>

              {/* Price */}
              <div className="mb-6">
                <span className="text-3xl font-bold text-white">€{pkg.price}</span>
              </div>

              {/* Buy button */}
              <Button
                className={`w-full bg-gradient-to-r ${getPackageColor(pkg.package_id)} hover:opacity-90 text-white font-bold uppercase tracking-wider`}
                onClick={() => handlePurchase(pkg.package_id)}
                disabled={purchasing || !canPurchase}
                data-testid={`buy-${pkg.package_id}`}
              >
                {purchasing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard size={18} className="mr-2" />
                    {isItalian ? 'Acquista' : 'Buy Now'}
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Remove Ads Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="max-w-md mx-auto mt-8"
      >
        <div className="bg-[#0a0d15] border border-white/10  overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-[#FF1744] to-[#FF5722]" />
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF1744] to-[#FF5722] flex items-center justify-center">
                {adsStatus.ads_removed ? (
                  <EyeOff size={28} className="text-white" />
                ) : (
                  <Ban size={28} className="text-white" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-white uppercase tracking-wider">
                  {isItalian ? 'Rimuovi Pubblicità' : 'Remove Ads'}
                </h3>
                <p className="text-sm text-slate-400">
                  {adsStatus.ads_removed 
                    ? (isItalian ? 'Pubblicità già rimosse!' : 'Ads already removed!')
                    : (isItalian ? 'Naviga senza interruzioni' : 'Browse without interruptions')
                  }
                </p>
              </div>
            </div>
            
            {!adsStatus.ads_removed && (
              <>
                <div className="flex items-center justify-center gap-2 mb-4 py-3 bg-[#050508]  border border-white/5">
                  <Coins size={20} className="text-[#FFEA00]" />
                  <span className="font-mono text-2xl font-bold text-white">
                    {(adsStatus.removal_cost || 50000).toLocaleString()}
                  </span>
                  <span className="text-slate-400 text-sm">coins</span>
                </div>
                
                <Button
                  className="w-full bg-gradient-to-r from-[#FF1744] to-[#FF5722] hover:opacity-90 text-white font-bold uppercase tracking-wider"
                  onClick={async () => {
                    setRemovingAds(true);
                    try {
                      await adsApi.remove();
                      toast.success(isItalian ? 'Pubblicità rimosse!' : 'Ads removed!');
                      setAdsStatus(prev => ({ ...prev, ads_removed: true }));
                      await refreshUser();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Errore');
                    } finally {
                      setRemovingAds(false);
                    }
                  }}
                  disabled={removingAds || (user?.personal_coins || 0) < (adsStatus.removal_cost || 50000)}
                  data-testid="remove-ads-btn"
                >
                  {removingAds ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Ban size={18} className="mr-2" />
                      {isItalian ? 'Rimuovi Pubblicità' : 'Remove Ads'}
                    </>
                  )}
                </Button>
                
                {(user?.personal_coins || 0) < (adsStatus.removal_cost || 50000) && (
                  <p className="text-xs text-[#FF1744] mt-2 text-center">
                    {isItalian 
                      ? `Hai ${(user?.personal_coins || 0).toLocaleString()} coins. Te ne servono ${(adsStatus.removal_cost || 50000).toLocaleString()}.`
                      : `You have ${(user?.personal_coins || 0).toLocaleString()} coins. You need ${(adsStatus.removal_cost || 50000).toLocaleString()}.`
                    }
                  </p>
                )}
              </>
            )}
            
            {adsStatus.ads_removed && (
              <div className="flex items-center justify-center gap-2 py-3 bg-[#00E676]/10  border border-[#00E676]/30">
                <CheckCircle size={20} className="text-[#00E676]" />
                <span className="text-[#00E676] font-bold">
                  {isItalian ? 'Attivo' : 'Active'}
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Payment info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center text-sm text-slate-500"
      >
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Zap size={16} />
            <span>{isItalian ? 'Consegna istantanea' : 'Instant delivery'}</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard size={16} />
            <span>{isItalian ? 'Pagamento sicuro con Stripe' : 'Secure payment with Stripe'}</span>
          </div>
          {paypalClientId && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .633-.54h6.012c2.65 0 4.46 1.37 4.46 3.69 0 3.56-2.9 5.52-5.78 5.52H8.16l-.64 4.73a.641.641 0 0 1-.633.54H5.1l-.85 4.17a.641.641 0 0 0 .633.74h2.193v-.27z"/>
              </svg>
              <span>PayPal</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Payment method selection modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-center">
              {isItalian ? 'Scegli Metodo di Pagamento' : 'Choose Payment Method'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPackage && (
            <div className="space-y-6 mt-4">
              {/* Package summary */}
              <div className="p-4 bg-[#050508] border border-white/10 ">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getPackageColor(selectedPackage.package_id)} flex items-center justify-center text-white`}>
                      {getPackageIcon(selectedPackage.package_id)}
                    </div>
                    <div>
                      <p className="font-bold text-white">{selectedPackage.name}</p>
                      <p className="text-sm text-[#FFEA00]">{selectedPackage.coins?.toLocaleString()} coins</p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">€{selectedPackage.price}</p>
                </div>
                {/* Show purchase type */}
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
                  {purchaseType === 'personal' ? (
                    <>
                      <User size={16} className="text-[#00F0FF]" />
                      <span className="text-sm text-slate-400">{isItalian ? 'Acquisto Personale' : 'Personal Purchase'}</span>
                    </>
                  ) : (
                    <>
                      <Shield size={16} className="text-[#00E676]" />
                      <span className="text-sm text-slate-400">{isItalian ? 'Per il club' : 'For club'}: {selectedClubData?.name}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Payment methods */}
              <div className="space-y-3">
                {/* Stripe option */}
                <button
                  onClick={handleStripePayment}
                  disabled={purchasing}
                  className="w-full p-4 bg-[#050508] border border-white/10  hover:border-[#00F0FF]/50 transition-colors flex items-center justify-between group"
                  data-testid="pay-stripe-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#635BFF] rounded flex items-center justify-center">
                      <CreditCard size={20} className="text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-white">Stripe</p>
                      <p className="text-xs text-slate-400">{isItalian ? 'Carta di credito/debito' : 'Credit/Debit card'}</p>
                    </div>
                  </div>
                  {purchasing ? (
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  ) : (
                    <span className="text-[#00F0FF] group-hover:translate-x-1 transition-transform">→</span>
                  )}
                </button>
                
                {/* PayPal option — redirect flow */}
                {paypalClientId ? (
                  <button
                    onClick={handlePayPalPayment}
                    disabled={paypalLoading}
                    className="w-full p-4 bg-[#050508] border border-white/10 hover:border-[#003087]/50 transition-colors group flex items-center justify-between cursor-pointer"
                    data-testid="pay-paypal-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#003087] rounded flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .633-.54h6.012c2.65 0 4.46 1.37 4.46 3.69 0 3.56-2.9 5.52-5.78 5.52H8.16l-.64 4.73a.641.641 0 0 1-.633.54H5.1l-.85 4.17a.641.641 0 0 0 .633.74h2.193v-.27z"/>
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">PayPal</p>
                        <p className="text-xs text-slate-400">{isItalian ? 'Paga con il tuo account PayPal' : 'Pay with your PayPal account'}</p>
                      </div>
                    </div>
                    {paypalLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    ) : (
                      <span className="text-[#00F0FF] group-hover:translate-x-1 transition-transform">→</span>
                    )}
                  </button>
                ) : (
                  <div className="w-full p-4 bg-[#050508]/50 border border-white/5  opacity-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#003087]/50 rounded flex items-center justify-center">
                        <svg className="w-5 h-5 text-white/50" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .633-.54h6.012c2.65 0 4.46 1.37 4.46 3.69 0 3.56-2.9 5.52-5.78 5.52H8.16l-.64 4.73a.641.641 0 0 1-.633.54H5.1l-.85 4.17a.641.641 0 0 0 .633.74h2.193v-.27z"/>
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-500">PayPal</p>
                        <p className="text-xs text-slate-600">{isItalian ? 'Non configurato' : 'Not configured'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment checking dialog */}
      <Dialog open={checkingPayment} onOpenChange={() => {}}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-center">
              {isItalian ? 'Verifica Pagamento' : 'Checking Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#00F0FF] mx-auto mb-4" />
            <p className="text-slate-400">
              {isItalian ? 'Stiamo verificando il tuo pagamento...' : 'Verifying your payment...'}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment result dialog */}
      <Dialog open={!!paymentResult} onOpenChange={() => setPaymentResult(null)}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider text-center">
              {paymentResult?.status === 'success' 
                ? (isItalian ? 'Pagamento Completato!' : 'Payment Complete!') 
                : (isItalian ? 'Pagamento' : 'Payment')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            {paymentResult?.status === 'success' ? (
              <>
                <CheckCircle className="w-16 h-16 text-[#00E676] mx-auto mb-4" />
                <p className="text-lg text-white mb-2">{paymentResult?.message}</p>
                <div className="flex items-center justify-center gap-2 text-[#FFEA00]">
                  <Coins size={24} />
                  <span className="font-mono text-2xl font-bold">
                    +{paymentResult?.coins?.toLocaleString()}
                  </span>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-[#FF1744] mx-auto mb-4" />
                <p className="text-slate-400 mb-4">{paymentResult?.message}</p>
                {paymentResult?.orderId && (
                  <Button
                    data-testid="retry-paypal-capture-btn"
                    className="w-full mb-2 bg-[#FFEA00] text-black hover:bg-[#FFEA00]/80 font-bold"
                    disabled={retryingCapture}
                    onClick={async () => {
                      setRetryingCapture(true);
                      try {
                        const response = await paypalApi.captureOrder({ order_id: paymentResult.orderId });
                        setPaymentResult({
                          status: 'success',
                          message: isItalian 
                            ? `Pagamento completato! ${response.data.coins_added?.toLocaleString()} coins aggiunti!`
                            : `Payment successful! ${response.data.coins_added?.toLocaleString()} coins added!`,
                          coins: response.data.coins_added,
                        });
                        if (refreshUser) refreshUser();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || (isItalian ? 'Errore. Riprova tra qualche secondo.' : 'Error. Try again in a few seconds.'));
                      } finally {
                        setRetryingCapture(false);
                      }
                    }}
                  >
                    {retryingCapture 
                      ? (isItalian ? 'Verifica in corso...' : 'Checking...')
                      : (isItalian ? 'Riprova Cattura Pagamento' : 'Retry Payment Capture')
                    }
                  </Button>
                )}
              </>
            )}
          </div>
          <Button 
            className="w-full btn-primary"
            onClick={() => setPaymentResult(null)}
          >
            {isItalian ? 'Chiudi' : 'Close'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Shop;
